import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {AdminUserLookupItem, ContractAdminListItem} from '@shared/api/models/query/contract.model';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';

/**
 * UNIVAPLAT-3518: modal that links users with the contract-admin role to a contract.
 *
 * Opened by the Contract admins tab via NgbModal (`ariaLabelledBy: 'link-contract-admins-title'`),
 * which supplies role=dialog, focus trap, Escape and focus return. Closes with `true` after a
 * successful add so the tab refreshes; Cancel, the close icon and Escape dismiss without a call.
 *
 * The contract-admin list items carry the link id, not the user id, so "already linked" users
 * are excluded by email (falling back to first + last name when a user has no email). The add
 * endpoint is idempotent for users already linked, so a miss here is harmless.
 *
 * Backend owns the user-facing error text. Names and emails are never logged and are dropped
 * when the modal is destroyed.
 */
@Component({
    selector: 'cdss-link-contract-admins-modal',
    standalone: true,
    imports: [SpinnerComponent],
    templateUrl: './link-contract-admins.modal.html'
})
export class LinkContractAdminsModal implements OnInit, OnDestroy {
    @Input() contractId!: number;
    /** The tab's current admin list, used to hide users that are already linked. */
    @Input() linkedAdmins: ContractAdminListItem[] = [];

    candidates: AdminUserLookupItem[] = [];
    loading = false;
    saving = false;
    loadError: string | null = null;

    private selected = new Set<number>();

    constructor(
        public modal: NgbActiveModal,
        private contractsService: ContractsService,
        private toastr: ToastrService,
    ) {
    }

    get selectedCount(): number {
        return this.selected.size;
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;
        try {
            const all = await this.contractsService.listCandidateAdmins();
            const linked = new Set(this.linkedAdmins.map(admin => this.identity(admin)));
            this.candidates = all.filter(user => !linked.has(this.identity(user)));
        } catch (error: any) {
            this.loadError = error?.error?.error ?? 'The users could not be loaded.';
        } finally {
            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this.candidates = [];
        this.selected.clear();
    }

    isSelected(user: AdminUserLookupItem): boolean {
        return this.selected.has(user.id);
    }

    toggle(user: AdminUserLookupItem, checked: boolean): void {
        if (checked) {
            this.selected.add(user.id);
        } else {
            this.selected.delete(user.id);
        }
    }

    userName(user: Pick<AdminUserLookupItem, 'firstName' | 'lastName'>): string {
        return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    }

    async submit(): Promise<void> {
        if (this.saving || this.loading || this.selected.size === 0) {
            return;
        }
        this.saving = true;
        try {
            await this.contractsService.addAdmins(this.contractId, Array.from(this.selected));
            this.toastr.success('Contract admins linked', 'Success');
            this.modal.close(true);
        } catch (error: any) {
            // Modal stays open so the selection can be corrected and retried.
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
        } finally {
            this.saving = false;
        }
    }

    private identity(user: {email: string | null, firstName: string | null, lastName: string}): string {
        const email = (user.email ?? '').trim().toLowerCase();
        return email !== '' ? `e:${email}` : `n:${(user.firstName ?? '').trim().toLowerCase()} ${user.lastName.trim().toLowerCase()}`;
    }
}
